import React, { Dispatch, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEqual, isNumber } from "lodash";

import MultiSelect from "../common/LocalMultiSelect";
import { Company, TaxiServiceOld as TaxiService } from "../../services";
import Map from "../../redux/services/Map";
import { useTaxiServiceFilterAccessControl } from "../../hooks";
import { getTaxiServiceLabel } from "../../utils";
import Filters from "../../pages/MainPage/pages/Preferences/tabs/Objects/components/Header/components/Filters";

function isAllCompanies(
	companies: Filters.Props["companies"],
): companies is ["all"] {
	return companies[0] === "all";
}

function isAllTaxiServices(
	taxiServices: Filters.Props["taxiServices"],
): taxiServices is ["all"] {
	return taxiServices[0] === "all";
}

const CompaniesAndTaxiServicesFilter: React.FC<
	CompaniesAndTaxiServicesFilter.Props
> = ({
	companies: selectedCompanies,
	taxiServices: selectedTaxiServices,
	language,
	children,
	onChangeCompanies,
	onChangeTaxiServices,
	display = "all",
	disabled = false,
}) => {
	const { t } = useTranslation();
	const { accessCompanies, accessTaxiServices } =
		useTaxiServiceFilterAccessControl<any>();

	const [companies, taxiServices] = [
		useMemo(() => accessCompanies, [accessCompanies]),
		useMemo(() => accessTaxiServices, [accessTaxiServices]),
	];
	// const { models: taxiServices } = useTypedSelector(
	// 	(state) => state.taxiServices,
	// );

	// const { items: companies } = useTypedSelector(
	// 	(state) => state.ordersPageReducer.companies,
	// );

	const processedSelectedCompanies = useMemo(
		() =>
			isAllCompanies(selectedCompanies)
				? companies?.map((company) => company.id) ?? []
				: selectedCompanies,
		[companies, selectedCompanies],
	);

	const processedSelectedTaxiServices = useMemo(
		() =>
			isAllTaxiServices(selectedTaxiServices)
				? taxiServices
						?.filter(
							(taxiService) =>
								isNumber(taxiService.company?.id) &&
								processedSelectedCompanies.includes(
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									taxiService.company!.id,
								),
						)
						.map((taxiService) => taxiService.id) ?? []
				: selectedTaxiServices,
		[selectedTaxiServices, taxiServices, processedSelectedCompanies],
	);

	const taxiServicesById = useMemo(
		() =>
			taxiServices?.reduce((accumulator, taxiService) => {
				accumulator[taxiService.id] = taxiService;

				return accumulator;
			}, {} as Record<TaxiService.Model["id"], TaxiService.Model>) ?? {},
		[taxiServices],
	);

	const companyOptions = useMemo(
		() => [
			...(companies?.map((company) => ({
				key: company.id,
				label: company.name?.[language] ?? "",
				value: company,
			})) ?? []),
		],
		[companies, language],
	);

	useEffect(() => {
		if (companyOptions.length === 1)
			onChangeCompanies([companyOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyOptions]);

	const filteredTaxiServices = useMemo(
		() =>
			isAllCompanies(selectedCompanies)
				? taxiServices
				: taxiServices?.filter(
						(taxiService) =>
							isNumber(taxiService.company?.id) &&
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							selectedCompanies.includes(taxiService.company!.id),
				  ),
		[selectedCompanies, taxiServices],
	);

	const taxiServiceOptions = useMemo(
		() => [
			...(filteredTaxiServices?.map((taxiService) => ({
				key: taxiService.id,
				label: getTaxiServiceLabel(taxiService, language),
				value: taxiService,
			})) ?? []),
		],
		[filteredTaxiServices, language],
	);

	useEffect(() => {
		if (taxiServiceOptions.length === 1)
			onChangeTaxiServices([taxiServiceOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taxiServiceOptions]);

	const companyMultiSelect = useMemo(
		() =>
			display !== "taxiServices" ? (
				<MultiSelect<Company.Model | Company.Model[]>
					value={processedSelectedCompanies}
					placeholder={t("companiesAndTaxiServicesFilter.str0") ?? ""}
					options={companyOptions}
					disabled={disabled || companyOptions.length === 1}
					all={companyOptions.length > 1}
					onChange={(value) => {
						let newCompanies: number[] | ["all"];

						if (
							companyOptions.every((companyOption) =>
								value.includes(companyOption.key),
							) &&
							companyOptions.length > 1
						)
							newCompanies = ["all"];
						else {
							newCompanies = value as number[];

							if (!isAllTaxiServices(selectedTaxiServices)) {
								let newTaxiServices: number[] | ["all"] =
									processedSelectedTaxiServices.filter(
										(taxiServiceId) =>
											isNumber(
												taxiServicesById[taxiServiceId]
													.company?.id,
											) &&
											(newCompanies as number[]).includes(
												// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
												taxiServicesById[taxiServiceId]
													.company!.id,
											),
									);

								const allPossibleTaxiServices =
									taxiServices
										?.filter(
											(taxiService) =>
												isNumber(
													taxiService.company?.id,
												) &&
												(
													newCompanies as number[]
												).includes(
													// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
													taxiService.company!.id,
												),
										)
										.map((taxiService) => taxiService.id) ??
									[];

								if (
									isEqual(
										allPossibleTaxiServices,
										newTaxiServices,
									)
								)
									newTaxiServices = ["all"];

								onChangeTaxiServices(newTaxiServices);
							}
						}
						onChangeCompanies(newCompanies);
					}}
				/>
			) : null,
		[
			display,
			processedSelectedCompanies,
			t,
			companyOptions,
			disabled,
			onChangeCompanies,
			selectedTaxiServices,
			processedSelectedTaxiServices,
			taxiServices,
			onChangeTaxiServices,
			taxiServicesById,
		],
	);

	const taxiServiceMultiSelect = useMemo(
		() =>
			display !== "companies" ? (
				<MultiSelect<TaxiService | TaxiService[] | undefined>
					value={processedSelectedTaxiServices}
					placeholder={t("companiesAndTaxiServicesFilter.str1") ?? ""}
					options={taxiServiceOptions}
					disabled={disabled || taxiServiceOptions.length <= 1}
					all={taxiServiceOptions.length > 1}
					onChange={(value) => {
						let newTaxiServices: number[] | ["all"];

						if (
							taxiServiceOptions.every((taxiServiceOption) =>
								value.includes(taxiServiceOption.key),
							) &&
							taxiServiceOptions.length > 1
						) {
							newTaxiServices = ["all"];
						} else {
							newTaxiServices = value as number[];
						}

						onChangeTaxiServices(newTaxiServices);
					}}
				/>
			) : null,
		[
			display,
			processedSelectedTaxiServices,
			t,
			taxiServiceOptions,
			disabled,
			onChangeTaxiServices,
		],
	);

	return children ? (
		children({ companyMultiSelect, taxiServiceMultiSelect })
	) : (
		<>
			{companyMultiSelect}
			{taxiServiceMultiSelect}
		</>
	);
};

declare namespace CompaniesAndTaxiServicesFilter {
	type Children = (options: Children.Options) => JSX.Element;

	namespace Children {
		interface Options {
			companyMultiSelect: JSX.Element | null;
			taxiServiceMultiSelect: JSX.Element | null;
		}
	}

	interface Props {
		companies: Company.Model["id"][] | ["all"];
		taxiServices: TaxiService.Model["id"][] | ["all"];

		language: Map.Language;
		children?: Children;

		onChangeCompanies: Dispatch<Company.Model["id"][] | ["all"]>;
		onChangeTaxiServices: Dispatch<TaxiService.Model["id"][] | ["all"]>;

		display?: "all" | "companies" | "taxiServices";
		disabled?: boolean;
	}
}

export default CompaniesAndTaxiServicesFilter;
